import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
 
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Formatting utilities
export function formatCpf(cpf: string): string {
  const numbers = cpf.replace(/\D/g, '');
  return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

// Phone formatting moved to @/lib/formatters for better organization
// Use formatPhone or formatPhoneDisplay from @/lib/formatters instead
export function formatPhone(phone: string): string {
  // Redirect to the improved function in formatters
  // This is kept for backward compatibility, but new code should use @/lib/formatters
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length <= 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  } else {
    // Celular: (XX) XXXXX-XXXX
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2')
      .replace(/(-\d{4})\d+?$/, '$1');
  }
}

export function formatCep(cep: string): string {
  const numbers = cep.replace(/\D/g, '');
  return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
}

// Cleanup utilities for backend submission
export function cleanCpf(cpf: string): string {
  return cpf.replace(/\D/g, '');
}

export function cleanPhone(phone: string): string {
  if (!phone || phone.trim() === '') return '';
  return phone.replace(/\D/g, '');
}

export function cleanCep(cep: string): string {
  return cep.replace(/\D/g, '');
}

/**
 * Converte uma Date para string ISO (YYYY-MM-DD) sem conversão UTC.
 * Previne o bug de timezone que faz a data ficar 1 dia atrás.
 */
export function dateToISOWithoutTimezone(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Converte uma string ISO (YYYY-MM-DD) para Date local sem shift de timezone.
 * Inverso de dateToISOWithoutTimezone.
 * new Date("2026-03-19") interpreta como UTC → mostra dia 18 no BR.
 * Esta função adiciona T12:00:00 para garantir que fique no dia correto em qualquer fuso.
 */
export function parseDateLocal(dateStr: string): Date {
  if (!dateStr) return new Date();
  // Se já tem "T", é um ISO completo — extrair só a parte da data
  const datePart = dateStr.split('T')[0];
  return new Date(`${datePart}T12:00:00`);
}

/** Converte ISO/DateTime do backend para valor de input type="date" (YYYY-MM-DD). */
export function toDateInputValue(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return dateStr.split('T')[0];
}

/**
 * Formata data para DD/MM/YYYY preservando exatamente a data salva no backend.
 * Ignora completamente o timezone: extrai literalmente YYYY-MM-DD da string ISO.
 * Evita o bug de "-1 dia" que acontece com new Date(iso).toLocaleDateString().
 */
export function formatDateBR(value?: string | Date | null): string {
  if (!value) return '';
  if (value instanceof Date) {
    return value.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}/${match[2]}/${match[1]}`;
  return new Date(value).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

// Date utilities
export function calculateAge(birthDate: Date): number {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }

  return age;
}

/**
 * Normaliza texto removendo acentos, pontuação e caracteres especiais
 * Útil para buscas case-insensitive e sem acentuação
 *
 * @example
 * normalizeText('João da Silva') // 'joao da silva'
 * normalizeText('Maria José (Santos)') // 'maria jose santos'
 * normalizeText('André & José') // 'andre jose'
 */
export function normalizeText(text: string): string {
  if (!text) return '';

  return text
    .toLowerCase()
    .normalize('NFD') // Decompõe caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Remove marcas diacríticas (acentos)
    .replace(/[^\w\s]/g, ' ') // Remove pontuação e caracteres especiais, mantém espaços
    .replace(/\s+/g, ' ') // Normaliza múltiplos espaços em um único
    .trim(); // Remove espaços das extremidades
}

/**
 * Converte texto em caixa alta (ou qualquer caso) para Title Case
 *
 * @example
 * toTitleCase('ESCOLA TESTE') // 'Escola Teste'
 * toTitleCase('escola municipal joão da silva') // 'Escola Municipal João Da Silva'
 */
export function toTitleCase(text: string): string {
  if (!text) return '';

  return text
    .toLowerCase()
    .split(' ')
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
    .join(' ')
}

/** Siglas institucionais que devem permanecer em caixa alta. */
const SIGLAS_INSTITUICAO = new Set([
  'EMEI', 'EMEII', 'EMEIF', 'EMEIEF', 'EMEF', 'EMEFM', 'EMEM',
  'CMEI', 'CMEII', 'CEI', 'CEMEI', 'CAIC', 'CIEP', 'APAE', 'EJA',
]);

/** Preposições/conjunções que ficam em minúsculo, exceto como primeira palavra. */
const PREPOSICOES_INSTITUICAO = new Set([
  'de', 'da', 'das', 'do', 'dos', 'e', 'em', 'para', 'por',
]);

/**
 * Converte o nome de uma instituição (escola, secretaria, etc.) para Title Case,
 * preservando siglas institucionais (EMEI, CMEI, APAE...) em caixa alta e
 * mantendo preposições/conjunções comuns em minúsculo (exceto na primeira palavra).
 * Apenas para exibição — não altera o valor original armazenado.
 *
 * @example
 * formatNomeInstituicao('ESCOLA MUNICIPAL DE EDUCAÇÃO INFANTIL E ENSINO FUNDAMENTAL')
 * // 'Escola Municipal de Educação Infantil e Ensino Fundamental'
 * formatNomeInstituicao('EMEIEF "SANTA JULIA"') // 'EMEIEF "Santa Julia"'
 */
export function formatNomeInstituicao(text: string): string {
  if (!text) return '';

  let wordIndex = 0;
  return text.toLowerCase().replace(/\p{L}+/gu, (word) => {
    const isFirst = wordIndex === 0;
    wordIndex += 1;

    const upper = word.toUpperCase();
    if (SIGLAS_INSTITUICAO.has(upper)) return upper;
    if (!isFirst && PREPOSICOES_INSTITUICAO.has(word)) return word;

    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}

/**
 * Converte um código de perfil/role (ex.: 'GESTOR_ESCOLA') para exibição em Title Case
 * (ex.: 'Gestor Escola'). Apenas para exibição — não altera o valor original.
 */
export function formatNomePerfil(text: string): string {
  if (!text) return '';
  return formatNomeInstituicao(text.replace(/_/g, ' '));
}

/** Paleta de cores para badges de perfil, escolhida de forma determinística por hash. */
const PALETA_BADGE_PERFIL = [
  'bg-blue-100 text-blue-700 border-blue-200',
  'bg-purple-100 text-purple-700 border-purple-200',
  'bg-amber-100 text-amber-700 border-amber-200',
  'bg-pink-100 text-pink-700 border-pink-200',
  'bg-cyan-100 text-cyan-700 border-cyan-200',
  'bg-indigo-100 text-indigo-700 border-indigo-200',
  'bg-rose-100 text-rose-700 border-rose-200',
  'bg-lime-100 text-lime-700 border-lime-200',
  'bg-teal-100 text-teal-700 border-teal-200',
  'bg-orange-100 text-orange-700 border-orange-200',
];

/** Cores fixas para perfis específicos, sobrepondo a escolha por hash. */
const CORES_BADGE_PERFIL_FIXAS: Record<string, string> = {
  GESTOR_ESCOLA: 'bg-blue-100 text-blue-700 border-blue-200',
};

/**
 * Escolhe uma cor de pill de forma determinística a partir de um texto (ex.: nome do perfil),
 * garantindo que o mesmo valor sempre receba a mesma cor, sem precisar de um mapa fixo
 * cobrindo todos os perfis possíveis. Perfis em `CORES_BADGE_PERFIL_FIXAS` usam a cor fixa.
 */
export function getPillColorClasses(text: string): string {
  if (!text) return PALETA_BADGE_PERFIL[0];

  const fixa = CORES_BADGE_PERFIL_FIXAS[text.toUpperCase()];
  if (fixa) return fixa;

  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    hash = (hash * 31 + text.charCodeAt(i)) >>> 0;
  }
  return PALETA_BADGE_PERFIL[hash % PALETA_BADGE_PERFIL.length];
}